/**
 * UserManager Component
 *
 * Admin component for managing users and their host access:
 * - User list display
 * - Host access assignment per user
 *
 * @module components/admin/UserManager
 */

import { useState, useCallback, useEffect, memo } from 'react';
import {
  Users,
  Server,
  RefreshCw,
  Loader2,
  AlertCircle,
  Search,
  Check,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Avatar } from '../common/Avatar.jsx';

// ============================================
// API Functions
// ============================================

async function fetchUsers() {
  const response = await fetch('/api/users', {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch users: ${response.statusText}`);
  }
  const data = await response.json();
  return data.data || [];
}

async function fetchHosts() {
  const response = await fetch('/api/hosts?includeDisabled=true', {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch hosts: ${response.statusText}`);
  }
  const data = await response.json();
  return data.data || [];
}

async function fetchHostAccess(hostId) {
  const response = await fetch(`/api/hosts/${hostId}/access`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch host access: ${response.statusText}`);
  }
  const data = await response.json();
  return data.data?.users || [];
}

async function grantHostAccess(hostId, userId) {
  const response = await fetch(`/api/hosts/${hostId}/access`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ userId })
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    // Backend returns { code, message, details, timestamp }
    // Ensure message is a string, not an object
    const rawMessage = error.message || error.error || response.statusText;
    const errorMessage = typeof rawMessage === 'string' ? rawMessage : JSON.stringify(rawMessage);
    throw new Error(errorMessage);
  }
  return response.json();
}

async function revokeHostAccess(hostId, userId) {
  const response = await fetch(`/api/hosts/${hostId}/access/${userId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    // Backend returns { code, message, details, timestamp }
    // Ensure message is a string, not an object
    const rawMessage = error.message || error.error || response.statusText;
    const errorMessage = typeof rawMessage === 'string' ? rawMessage : JSON.stringify(rawMessage);
    throw new Error(errorMessage);
  }
}

// ============================================
// Sub-Components
// ============================================

const SearchInput = memo(function SearchInput({
  value,
  onChange,
  placeholder = 'Search users...'
}) {
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="
          w-full pl-9 pr-3 py-2 rounded-md
          bg-gray-900 border border-gray-700 text-gray-100
          placeholder-gray-500
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
          transition-colors
        "
      />
    </div>
  );
});

const LoadingSkeleton = memo(function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="flex items-center gap-4 p-4 rounded-lg bg-gray-800/50 animate-pulse"
        >
          <div className="w-10 h-10 rounded-full bg-gray-700" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-32 bg-gray-700 rounded" />
            <div className="h-3 w-48 bg-gray-700 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
});

const ErrorState = memo(function ErrorState({ message, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="p-3 rounded-full bg-red-500/10 mb-4">
        <AlertCircle className="w-8 h-8 text-red-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-300 mb-1">
        Failed to load users
      </h3>
      <p className="text-sm text-red-400 mb-4">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="
            flex items-center gap-2 px-4 py-2 rounded-md
            text-gray-300 bg-gray-700 hover:bg-gray-600
            transition-colors
          "
        >
          <RefreshCw className="w-4 h-4" />
          Retry
        </button>
      )}
    </div>
  );
});

const EmptyState = memo(function EmptyState({ message, description }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="p-3 rounded-full bg-gray-800 mb-4">
        <Users className="w-8 h-8 text-gray-500" />
      </div>
      <h3 className="text-lg font-medium text-gray-300 mb-1">{message}</h3>
      <p className="text-sm text-gray-500">{description}</p>
    </div>
  );
});

const HostAccessCheckbox = memo(function HostAccessCheckbox({
  host,
  userId,
  hasAccess,
  onToggle,
  isLoading
}) {
  return (
    <label
      className={`
        flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer
        ${hasAccess
          ? 'bg-green-500/20 border border-green-500/30'
          : 'bg-gray-800 border border-gray-700 hover:bg-gray-700'
        }
        ${isLoading ? 'opacity-50 cursor-wait' : ''}
        transition-colors
      `}
    >
      <input
        type="checkbox"
        checked={hasAccess}
        onChange={() => onToggle(host.id, userId, !hasAccess)}
        disabled={isLoading}
        className="sr-only"
      />
      <div
        className={`
          w-4 h-4 rounded flex items-center justify-center
          ${hasAccess
            ? 'bg-green-500 text-white'
            : 'bg-gray-700 border border-gray-600'
          }
        `}
      >
        {hasAccess && <Check className="w-3 h-3" />}
      </div>
      <Server className={`w-4 h-4 ${hasAccess ? 'text-green-400' : 'text-gray-400'}`} />
      <span className={`text-sm ${hasAccess ? 'text-green-300' : 'text-gray-300'}`}>
        {host.displayName || host.name}
      </span>
      {isLoading && <Loader2 className="w-3 h-3 animate-spin text-gray-400 ml-auto" />}
    </label>
  );
});

const UserRow = memo(function UserRow({
  user,
  hosts,
  userHostAccess,
  onToggleAccess,
  loadingHostId,
  isExpanded,
  onToggleExpand
}) {
  const accessCount = hosts.filter(h => userHostAccess[user.id]?.includes(h.id)).length;

  return (
    <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg overflow-hidden">
      {/* User header */}
      <div
        className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-800 transition-colors"
        onClick={onToggleExpand}
      >
        <Avatar
          name={user.displayName || user.username}
          avatarUrl={user.avatarUrl}
          type="user"
          size="md"
        />
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-white truncate">
            {user.displayName || user.username}
          </h3>
          <p className="text-sm text-gray-400">@{user.username}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-400">
            {accessCount} host{accessCount !== 1 ? 's' : ''}
          </span>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Host access grid */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-700/50">
          <p className="text-xs text-gray-500 mb-3">Host Access</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {hosts.map((host) => (
              <HostAccessCheckbox
                key={host.id}
                host={host}
                userId={user.id}
                hasAccess={userHostAccess[user.id]?.includes(host.id) || false}
                onToggle={onToggleAccess}
                isLoading={loadingHostId === host.id}
              />
            ))}
          </div>
          {hosts.length === 0 && (
            <p className="text-sm text-gray-500 italic">No hosts available</p>
          )}
        </div>
      )}
    </div>
  );
});

// ============================================
// Main Component
// ============================================

export default function UserManager() {
  const [users, setUsers] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [userHostAccess, setUserHostAccess] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [loadingHostId, setLoadingHostId] = useState(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [usersData, hostsData] = await Promise.all([
        fetchUsers(),
        fetchHosts()
      ]);
      setUsers(usersData);
      setHosts(hostsData);

      // Load access for all hosts
      const accessMap = {};
      await Promise.all(
        hostsData.map(async (host) => {
          try {
            const usersWithAccess = await fetchHostAccess(host.id);
            usersWithAccess.forEach((u) => {
              // Backend returns 'id' not 'userId'
              const uid = u.id || u.userId;
              if (!accessMap[uid]) {
                accessMap[uid] = [];
              }
              accessMap[uid].push(host.id);
            });
          } catch {
            // Ignore errors for individual host access
          }
        })
      );
      setUserHostAccess(accessMap);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleAccess = useCallback(async (hostId, userId, shouldGrant) => {
    setLoadingHostId(hostId);
    try {
      if (shouldGrant) {
        await grantHostAccess(hostId, userId);
        setUserHostAccess((prev) => ({
          ...prev,
          [userId]: [...(prev[userId] || []), hostId]
        }));
      } else {
        await revokeHostAccess(hostId, userId);
        setUserHostAccess((prev) => ({
          ...prev,
          [userId]: (prev[userId] || []).filter((id) => id !== hostId)
        }));
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setLoadingHostId(null);
    }
  }, []);

  const filteredUsers = users.filter((user) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.username?.toLowerCase().includes(query) ||
      user.displayName?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-indigo-500/10">
            <Users className="w-6 h-6 text-indigo-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white">Users</h1>
            <p className="text-sm text-gray-400">
              {users.length} user{users.length !== 1 ? 's' : ''}, {hosts.length} host{hosts.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={loadData}
          disabled={isLoading}
          className="
            p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-colors
          "
          title="Refresh"
        >
          <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Search */}
      <div className="max-w-md">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search users..."
        />
      </div>

      {/* Content */}
      {isLoading && users.length === 0 ? (
        <LoadingSkeleton />
      ) : error ? (
        <ErrorState message={error} onRetry={loadData} />
      ) : filteredUsers.length === 0 ? (
        <EmptyState
          message={searchQuery ? 'No users match your search' : 'No users found'}
          description={searchQuery ? 'Try adjusting your search query.' : 'Users will appear here once they log in.'}
        />
      ) : (
        <div className="space-y-3">
          {filteredUsers.map((user) => (
            <UserRow
              key={user.id}
              user={user}
              hosts={hosts}
              userHostAccess={userHostAccess}
              onToggleAccess={handleToggleAccess}
              loadingHostId={expandedUserId === user.id ? loadingHostId : null}
              isExpanded={expandedUserId === user.id}
              onToggleExpand={() => setExpandedUserId(
                expandedUserId === user.id ? null : user.id
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export { UserManager };
